import type {
  Discovery,
  DiscoveryMode,
  DiscoverySessionContext,
} from "@/domain/types";

export const DISCOVERY_STORAGE_VERSION = 1;
export const DISCOVERY_STORAGE_PREFIX = "qotta.discovery.v1";

type BrowserStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

type StoredDiscovery = {
  version: typeof DISCOVERY_STORAGE_VERSION;
  customerId: string;
  discovery: Omit<
    Discovery,
    | "startedAt"
    | "firstAnswerAt"
    | "completedAt"
    | "contactRequestedAt"
    | "updatedAt"
    | "expiresAt"
  > & {
    startedAt: string;
    firstAnswerAt: string | null;
    completedAt: string | null;
    contactRequestedAt: string | null;
    updatedAt: string;
    expiresAt: string;
  };
};

export type DiscoveryPersistenceTarget = "local" | "firestore";

function browserStorage(): BrowserStorage | null {
  return typeof window === "undefined" ? null : window.localStorage;
}

function date(value: unknown): Date | null {
  if (typeof value !== "string") return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function storageContext(context: DiscoverySessionContext): string {
  if (context.sessionId) return `session:${context.sessionId}`;
  return [context.mode, context.sourceUrl, context.sourcePath].join("|");
}

export function discoveryStorageKey(
  customerId: string,
  context: DiscoverySessionContext,
): string {
  return `${DISCOVERY_STORAGE_PREFIX}.${encodeURIComponent(customerId)}.${encodeURIComponent(storageContext(context))}`;
}

export function discoveryPersistenceTarget(
  mode: DiscoveryMode,
  authenticatedUserId: string | null,
): DiscoveryPersistenceTarget {
  if (mode === "live" || mode === "demo") return "firestore";
  if (mode === "simulator" && authenticatedUserId) return "firestore";
  return "local";
}

export function saveLocalDiscovery(
  customerId: string,
  key: string,
  discovery: Discovery,
  storage: BrowserStorage | null = browserStorage(),
): void {
  if (!storage) return;
  const stored: StoredDiscovery = {
    version: DISCOVERY_STORAGE_VERSION,
    customerId,
    discovery: {
      ...discovery,
      startedAt: discovery.startedAt.toISOString(),
      firstAnswerAt: discovery.firstAnswerAt?.toISOString() ?? null,
      completedAt: discovery.completedAt?.toISOString() ?? null,
      contactRequestedAt: discovery.contactRequestedAt?.toISOString() ?? null,
      updatedAt: discovery.updatedAt.toISOString(),
      expiresAt: discovery.expiresAt.toISOString(),
    },
  };
  storage.setItem(key, JSON.stringify(stored));
}

export function loadLocalDiscovery(
  customerId: string,
  key: string,
  storage: BrowserStorage | null = browserStorage(),
): Discovery | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<StoredDiscovery>;
    const discovery = value.discovery as Record<string, unknown> | undefined;
    const startedAt = date(discovery?.["startedAt"]);
    const updatedAt = date(discovery?.["updatedAt"]);
    const expiresAt = date(discovery?.["expiresAt"]);
    if (
      value.version !== DISCOVERY_STORAGE_VERSION ||
      value.customerId !== customerId ||
      !discovery ||
      typeof discovery["id"] !== "string" ||
      typeof discovery["sessionId"] !== "string" ||
      !startedAt ||
      !updatedAt ||
      !expiresAt
    ) {
      storage.removeItem(key);
      return null;
    }
    const result = discovery["result"] as Discovery["result"] | undefined;
    return {
      ...(discovery as unknown as Discovery),
      result: result
        ? {
            ...result,
            generatedAt:
              date((result as unknown as Record<string, unknown>)["generatedAt"]) ?? new Date(0),
          }
        : null,
      startedAt,
      firstAnswerAt: date(discovery["firstAnswerAt"]),
      completedAt: date(discovery["completedAt"]),
      contactRequestedAt: date(discovery["contactRequestedAt"]),
      updatedAt,
      expiresAt,
    };
  } catch {
    storage.removeItem(key);
    return null;
  }
}

export function removeLocalDiscovery(
  key: string,
  storage: BrowserStorage | null = browserStorage(),
): void {
  storage?.removeItem(key);
}
